import {FC, useState, useEffect, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import Swal from 'sweetalert2'
import {KTSVG} from '../../../helpers'
import {Step1} from './steps/Step1'
import {Step2} from './steps/Step2'
import {Step3} from './steps/Step3'
import {Project, initialProject} from '../../../../app/modules/project/components/core/_models'
import {createProject} from '../../../../app/modules/project/components/core/_requests'
import {Toast} from '../../widgets'
import {Loading} from '../../components/loading/Loading'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {TaskTemplatesStep} from './steps/TaskTemplatesStep'
import {useGetProjectTemplatesBootstrap} from '../../../../app/modules/project-templates/hooks/useGetProjectTemplates'
import {useHasModule} from '../../../../app/modules/auth'
import {Alert, Form, Spinner} from 'react-bootstrap'
import {importTrelloToProject, importClickUpToProject} from '../../../../app/modules/integrations/services/projectImportService'

type Props = {
  handleClose?: () => void | undefined
  redirectAfterCreate?: boolean
  trelloBoard?: any // Dados do board Trello selecionado (opcional)
  trelloMapping?: any // Dados do mapeamento das listas Trello (opcional)
  clickUpList?: any // Dados da lista ClickUp selecionada (opcional)
  clickUpMapping?: any // Dados do mapeamento das tasks ClickUp (opcional)
  projectTemplate?: any // Dados do template de projeto (opcional)
}

const ProjectModalContent: FC<Props> = ({
  handleClose,
  redirectAfterCreate,
  trelloBoard,
  trelloMapping,
  clickUpList,
  clickUpMapping,
  projectTemplate,
}) => {
  const intl = useIntl()
  const [data, setData] = useState<Project>({...initialProject, name: projectTemplate?.name ?? ''})
  const [hasError, setHasError] = useState<boolean>(false)
  const [isSubmitting, setSubmitting] = useState<boolean>(false)
  const [selectedTemplateData, setSelectedTemplateData] = useState<any>(projectTemplate || null)
  const [currentStep, setCurrentStep] = useState<number>(1)

  const hasProjectTemplatesModule = useHasModule('modelos-de-projetos')
  const {data: templates = [], isLoading, isError} = useGetProjectTemplatesBootstrap(200, hasProjectTemplatesModule)

  useEffect(() => {
    if (!projectTemplate?.id) {
      return
    }
    setSelectedTemplateData((prev: any) => {
      if (prev && Number(prev.id) === Number(projectTemplate.id)) {
        const incoming = projectTemplate.task_templates
        if (Array.isArray(incoming) && incoming.length > 0) {
          return {...prev, ...projectTemplate, task_templates: incoming}
        }
        return {...prev, ...projectTemplate}
      }
      if (!prev) {
        return projectTemplate
      }
      return prev
    })
  }, [projectTemplate])

  // Merge list data into the selected project template without wiping the selection while
  // `templates` is still loading, and preserve `task_templates` when the list payload omits them.
  useEffect(() => {
    if (!selectedTemplateData?.id) {
      return
    }
    if (!Array.isArray(templates) || templates.length === 0) {
      return
    }

    const template = templates.find(
      (t: any) => Number(t.id) === Number(selectedTemplateData.id)
    )

    if (!template) {
      return
    }

    setSelectedTemplateData((prev: any) => {
      if (!prev || Number(prev.id) !== Number(template.id)) {
        return prev
      }
      const listTasks = template.task_templates
      const prevTasks = prev.task_templates
      return {
        ...prev,
        ...template,
        task_templates:
          Array.isArray(listTasks) && listTasks.length > 0
            ? listTasks
            : Array.isArray(prevTasks)
              ? prevTasks
              : [],
      }
    })
  }, [templates, selectedTemplateData?.id])

  useEffect(() => {
    if (!projectTemplate?.id) {
      return
    }
    const fromTasks = projectTemplate.task_templates?.map((t: any) => Number(t.id))
    const fromIds = (projectTemplate.task_template_ids || []).map((x: number) => Number(x))
    const ids =
      Array.isArray(fromTasks) && fromTasks.length > 0
        ? fromTasks
        : fromIds.filter((n: number) => !Number.isNaN(n))
    if (!ids.length) {
      return
    }
    setData((prev) => ({
      ...prev,
      task_template_ids: ids,
    }))
  }, [projectTemplate])

  // ✅ IMPORTAÇÃO TRELLO: Estados para controle do loading
  const [isImporting, setIsImporting] = useState<boolean>(false)
  const [importProgress, setImportProgress] = useState<
    | {
        total: number
        current: number
        currentCard?: string
      }
    | undefined
  >(undefined)

  // ✅ IMPORTAÇÃO CLICKUP: Estados para controle do loading
  const [isImportingClickUp, setIsImportingClickUp] = useState<boolean>(false)
  const [importProgressClickUp, setImportProgressClickUp] = useState<
    | {
        total: number
        current: number
        currentTask?: string
      }
    | undefined
  >(undefined)

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // ✅ IMPORTAÇÃO TRELLO: Pré-preenche nome do projeto quando board for selecionado
  useEffect(() => {
    if (trelloBoard?.name) {
      updateData({name: trelloBoard.name})
    }
  }, [trelloBoard])

  // ✅ IMPORTAÇÃO CLICKUP: Pré-preenche nome do projeto quando lista for selecionada
  useEffect(() => {
    if (clickUpList?.name) {
      updateData({name: clickUpList.name})
    }
  }, [clickUpList])

  // ✅ IMPORTAÇÃO TRELLO: Mostra informações sobre o mapeamento quando disponível
  useEffect(() => {
    if (trelloMapping) {
      // Aqui podemos adicionar lógica adicional para pré-preencher outros campos
    }
  }, [trelloMapping])

  // ✅ IMPORTAÇÃO CLICKUP: Mostra informações sobre o mapeamento quando disponível
  useEffect(() => {
    if (clickUpMapping) {
      // Aqui podemos adicionar lógica adicional para pré-preencher outros campos
    }
  }, [clickUpMapping])

  // ✅ IMPORTAÇÃO TRELLO: usa serviço compartilhado
  const importTrelloCards = async (projectId: number) => {
    if (!trelloMapping || !trelloMapping.cards) {
      console.error('❌ Dados de mapeamento do Trello não encontrados')
      return
    }
    try {
      setIsImporting(true)
      setImportProgress({total: 0, current: 0})
      await importTrelloToProject(trelloMapping, projectId, (p) =>
        setImportProgress({
          total: p.total,
          current: p.current,
          currentCard: p.currentItem,
        })
      )
    } catch (error) {
      console.error('❌ Erro ao importar cards do Trello:', error)
    } finally {
      setIsImporting(false)
      setImportProgress(undefined)
    }
  }

  // ✅ IMPORTAÇÃO CLICKUP: usa serviço compartilhado
  const importClickUpTasks = async (projectId: number) => {
    if (!clickUpMapping || !clickUpMapping.tasks) {
      console.error('❌ Dados de mapeamento do ClickUp não encontrados')
      return
    }
    try {
      setIsImportingClickUp(true)
      setImportProgressClickUp({total: 0, current: 0})
      await importClickUpToProject(clickUpMapping, projectId, (p) =>
        setImportProgressClickUp({
          total: p.total,
          current: p.current,
          currentTask: p.currentItem,
        })
      )
    } catch (error) {
      console.error('❌ Erro ao importar tasks do ClickUp:', error)
    } finally {
      setIsImportingClickUp(false)
      setImportProgressClickUp(undefined)
    }
  }

  const updateData = useCallback((fieldsToUpdate: any) => {
    setData((prev) => ({...prev, ...fieldsToUpdate}))
  }, [])

  const checkProjectBasic = (): boolean => {
    if (!data.name || !data.client_id) return false

    return true
  }

  const checkProjectComplements = () => {
    if (!data.begin_date) return false

    return true
  }

  const prevStep = () => {
    setCurrentStep((prev) => prev - 1)
  }

  const nextStep = () => {
    setHasError(false)

    if (currentStep === 1) {
      if (!checkProjectBasic()) {
        setHasError(true)
        return
      }
    }

    setCurrentStep((prev) => prev + 1)
  }

  const create = useMutation(() => createProject(data), {
    onSuccess: async (response) => {
      setSubmitting(false)
      setData(initialProject)

      // ✅ IMPORTAÇÃO TRELLO: Se houver mapeamento, importa os cards
      if (trelloMapping && response?.project?.id) {
        console.log('🚀 Projeto criado, iniciando importação dos cards...')
        await importTrelloCards(Number(response.project.id))
      }

      // ✅ IMPORTAÇÃO CLICKUP: Se houver mapeamento, importa as tasks
      if (clickUpMapping && response?.project?.id) {
        console.log('🚀 Projeto criado, iniciando importação das tasks...')
        await importClickUpTasks(Number(response.project.id))
      }

      Toast.fire({
        icon: 'success',
        title: intl.formatMessage(
          {id: 'ALERT.LABEL.SUCCESS_CREATE_ITEM'},
          {item: intl.formatMessage({id: 'TABLE.GENERAL.LABEL.PROJECT'})}
        ),
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
      if (handleClose) {
        handleClose()
      }
      queryClient.invalidateQueries()
      setCurrentStep(1)
      if (redirectAfterCreate) {
        navigate(`/projects/${response?.project.id}`)
      }
    },
    onError: (error: any) => {
      setSubmitting(false)
      Swal.fire({
        icon: 'warning',
        text: error.response.data.message,
      })
    },
  })

  const submit = () => {
    setHasError(false)

    if (!checkProjectComplements()) {
      setHasError(true)
      return
    } else {
      setSubmitting(true)
      return create.mutateAsync()
    }
  }

  return (
    <>
      <div className='modal-body'>
        {/*begin::Stepper */}
        <div
          className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid'
          id='kt_modal_edit_project_stepper'
        >
          {/* begin::Aside*/}
          <div className='d-flex flex-row-auto w-100 w-xl-300px'>
            {/* begin::Nav*/}
            <div className='stepper-nav ps-lg-10 py-5'>
              {/* begin::Step 1*/}
              <div
                className={`stepper-item ${currentStep == 1 && 'current'}`}
                data-kt-stepper-element='nav'
              >
                {/* begin::Wrapper*/}
                <div className='stepper-wrapper'>
                  {/* begin::Icon*/}
                  <div className='stepper-icon w-40px h-40px'>
                    {currentStep > 1 && <i className='fas fa-check text-primary'></i>}
                    {currentStep == 1 && <span className=''>1</span>}
                  </div>
                  {/* end::Icon*/}

                  {/* begin::Label*/}
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>
                      {intl.formatMessage({id: 'FORM.STEP.NAME.DETAILS'})}
                    </h3>

                    <div className='stepper-desc'>
                      {intl.formatMessage({id: 'FORM.STEP.DESCRIPTION.DETAILS'})}
                    </div>
                  </div>
                  {/* end::Label*/}
                </div>
                {/* end::Wrapper*/}

                {/* begin::Line*/}
                <div className='stepper-line h-40px'></div>
                {/* end::Line*/}
              </div>
              {/* end::Step 1*/}

              {!!selectedTemplateData && (
                <div
                  className={`stepper-item ${currentStep == 2 && 'current'}`}
                  data-kt-stepper-element='nav'
                >
                  <div className='stepper-wrapper'>
                    <div className='stepper-icon w-40px h-40px'>
                      {currentStep > 2 ? (
                        <i className='fas fa-check text-primary'></i>
                      ) : (
                        <span className=''>2</span>
                      )}
                    </div>

                    <div className='stepper-label'>
                      <h3 className='stepper-title'>Modelos de Tarefas</h3>

                      <div className='stepper-desc'>
                        Selecione as tarefas que farão parte do projeto
                      </div>
                    </div>
                  </div>

                  <div className='stepper-line h-40px'></div>
                </div>
              )}

              {/* begin::Step 2*/}
              <div
                className={`stepper-item ${
                  currentStep == (!!selectedTemplateData ? 3 : 2) && 'current'
                }`}
                data-kt-stepper-element='nav'
              >
                {/* begin::Wrapper*/}
                <div className='stepper-wrapper'>
                  {/* begin::Icon*/}
                  <div className='stepper-icon w-40px h-40px'>
                    {currentStep > (!!selectedTemplateData ? 3 : 2) ? (
                      <i className='fas fa-check text-primary'></i>
                    ) : (
                      <span className=''>{!!selectedTemplateData ? '3' : '2'}</span>
                    )}
                  </div>
                  {/* begin::Icon*/}

                  {/* begin::Label*/}
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>
                      {intl.formatMessage({id: 'FORM.STEP.NAME.TYPE'})}
                    </h3>

                    <div className='stepper-desc'>
                      {intl.formatMessage({id: 'FORM.STEP.DESCRIPTION.TYPE'})}
                    </div>
                  </div>
                  {/* begin::Label*/}
                </div>
                {/* end::Wrapper*/}

                {/* begin::Line*/}
                <div className='stepper-line h-40px'></div>
                {/* end::Line*/}
              </div>
              {/* end::Step 2*/}

              {/* begin::Step 3*/}
              <div
                className={`stepper-item ${
                  currentStep == (!!selectedTemplateData ? 4 : 3) && 'current'
                }`}
                data-kt-stepper-element='nav'
              >
                {/* begin::Wrapper*/}
                <div className='stepper-wrapper'>
                  {/* begin::Icon*/}
                  <div className='stepper-icon w-40px h-40px'>
                    {currentStep > (!!selectedTemplateData ? 4 : 3) ? (
                      <i className='fas fa-check text-primary'></i>
                    ) : (
                      <span className=''>{!!selectedTemplateData ? '4' : '3'}</span>
                    )}
                  </div>
                  {/* end::Icon*/}

                  {/* begin::Label*/}
                  <div className='stepper-label'>
                    <h3 className='stepper-title'>
                      {intl.formatMessage({id: 'FORM.STEP.NAME.INFORMATION'})}
                    </h3>

                    <div className='stepper-desc'>
                      {intl.formatMessage({id: 'FORM.STEP.DESCRIPTION.INFORMATION'})}
                    </div>
                  </div>
                  {/* end::Label*/}
                </div>
                {/* end::Wrapper*/}
              </div>
              {/* end::Step 3*/}
            </div>
            {/* end::Nav*/}
          </div>
          {/* begin::Aside*/}

          {/*begin::Content */}

          <div className='flex-row-fluid py-lg-5 px-lg-15'>
            {/*begin::Form */}
            <form noValidate id='kt_modal_edit_project_form'>
              {currentStep == 1 && (
                <Step1 data={data} updateData={updateData} hasError={hasError} />
              )}

              {!!selectedTemplateData && currentStep == 2 && (
                <TaskTemplatesStep
                  data={data}
                  updateData={updateData}
                  taskTemplates={selectedTemplateData.task_templates ?? []}
                  hasError={hasError}
                />
              )}

              {((!!selectedTemplateData && currentStep == 3) ||
                (!selectedTemplateData && currentStep == 2)) && (
                <Step2 data={data} updateData={updateData} hasError={hasError} />
              )}

              {((!!selectedTemplateData && currentStep == 4) ||
                (!selectedTemplateData && currentStep == 3)) && (
                <Step3
                  data={data}
                  updateData={updateData}
                  hasError={hasError}
                  isImporting={isImporting || isImportingClickUp}
                  importProgress={importProgress || importProgressClickUp}
                />
              )}

              {/*begin::Actions */}
              <div className='d-flex flex-md-stack flex-column flex-md-row gap-5 pt-10'>
                <div className='me-2'>
                  {currentStep === 1 && hasProjectTemplatesModule && (
                    <Form.Group className=''>
                      {isLoading ? (
                        <Spinner animation='border' size='sm' />
                      ) : isError ? (
                        <Alert variant='danger'>Erro ao carregar modelos</Alert>
                      ) : (
                        <Form.Select
                          className='pe-8'
                          value={selectedTemplateData?.id || ''}
                          onChange={(e) => {
                            const selected = templates.find(
                              (template: any) => template.id == e.target.value
                            )
                            setSelectedTemplateData(selected)
                            updateData({
                              name: selected?.name ?? '',
                              task_template_ids:
                                selected?.task_templates?.map((t: any) => t.id) ?? [],
                            })
                          }}
                        >
                          <option value=''>Criar através de um template</option>
                          {templates.map((template: any) => (
                            <option key={template.id} value={template.id}>
                              {template.name}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </Form.Group>
                  )}

                  {currentStep > 1 && (
                    <button
                      type='button'
                      className='btn btn-lg btn-light-primary me-3'
                      onClick={prevStep}
                    >
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr063.svg'
                        className='svg-icon-3 me-1'
                      />{' '}
                      {intl.formatMessage({id: 'GENERAL.BACK'})}
                    </button>
                  )}
                </div>
                <div className='text-end'>
                  {((selectedTemplateData && currentStep == 4) ||
                    (!selectedTemplateData && currentStep == 3)) && (
                    <button type='button' className='btn btn-lg btn-primary' onClick={submit}>
                      {intl.formatMessage({id: 'FORM.GENERAL.SAVE_BUTTON'})}{' '}
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr064.svg'
                        className='svg-icon-3 ms-2 me-0'
                      />
                    </button>
                  )}

                  {((selectedTemplateData && currentStep <= 3) ||
                    (!selectedTemplateData && currentStep <= 2)) && (
                    <button type='button' className='btn btn-lg btn-primary' onClick={nextStep}>
                      {intl.formatMessage({id: 'GENERAL.NEXT_STEP'})}{' '}
                      <KTSVG
                        path='/media/icons/duotune/arrows/arr064.svg'
                        className='svg-icon-3 ms-1 me-0'
                      />
                    </button>
                  )}
                </div>
              </div>
              {/*end::Actions */}
            </form>
            {/*end::Form */}
          </div>

          {/*end::Content */}
        </div>
        {/* end::Stepper */}
      </div>
      {isSubmitting && <Loading />}
    </>
  )
}

export {ProjectModalContent}
